// src/pages/CampingBookingSuccess.js
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';

const CampingBookingSuccess = () => {
    const { bookingId } = useParams();
    const location = useLocation();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchBookingDetails = useCallback(async () => {
        try {
            // Try to get from API first
            const response = await axios.get(buildApiUrl(`/camping/booking/${bookingId}`));
            if (response.data.success) {
                setBooking(response.data.data);
            } else if (location.state) {
                // Fallback to passed state
                setBooking(location.state);
            }
        } catch (error) {
            console.error('Error fetching booking:', error);
            if (location.state) {
                setBooking(location.state);
            }
        } finally {
            setLoading(false);
        }
    }, [bookingId, location.state]);

    useEffect(() => {
        fetchBookingDetails();
    }, [fetchBookingDetails]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };

    if (loading) {
        return (
            <div className="camping-success-loading">
                <div className="spinner"></div>
                <p>Loading booking details...</p>
            </div>
        );
    }

    return (
        <div className="camping-booking-success-page">
            <div className="camping-success-container">
                <div className="success-icon">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                </div>
                
                <h1>Camping Booking Confirmed! 🏕️</h1>
                <p className="success-message">Your camping adventure is all set! We can't wait to host you.</p>
                
                <div className="booking-card">
                    <h3>Booking Details</h3>
                    
                    <div className="booking-info-grid">
                        <div className="info-row">
                            <span className="label">Booking ID:</span>
                            <span className="value">{booking?.bookingId || booking?.booking_id || bookingId}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Booking Type:</span>
                            <span className="value camping-badge">⛺ Camping</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Customer Name:</span>
                            <span className="value">{booking?.customer_name || booking?.customerName}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Email:</span>
                            <span className="value">{booking?.customer_email || booking?.customerEmail}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Phone:</span>
                            <span className="value">{booking?.customer_phone || booking?.customerPhone}</span>
                        </div>
                    </div>

                    <div className="divider"></div>

                    <h3>Camping Site Details</h3>
                    <div className="booking-info-grid">
                        <div className="info-row">
                            <span className="label">Site Name:</span>
                            <span className="value">{booking?.site_name || booking?.siteName}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Location:</span>
                            <span className="value">{booking?.location || `${booking?.location_name}, ${booking?.location_address}`}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Check-in:</span>
                            <span className="value">{formatDate(booking?.check_in || booking?.checkIn)}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Check-out:</span>
                            <span className="value">{formatDate(booking?.check_out || booking?.checkOut)}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Nights:</span>
                            <span className="value">{booking?.total_nights || booking?.nights}</span>
                        </div>
                        <div className="info-row">
                            <span className="label">Guests:</span>
                            <span className="value">{booking?.guests}</span>
                        </div>
                    </div>

                    <div className="divider"></div>

                    <div className="price-summary">
                        <div className="price-row">
                            <span>Total Amount:</span>
                            <span className="total-price">{formatPrice(booking?.total_amount || booking?.totalAmount)}</span>
                        </div>
                        <div className="price-row paid">
                            <span>Amount Paid:</span>
                            <span>{formatPrice(booking?.total_amount || booking?.totalAmount)}</span>
                        </div>
                    </div>

                    {booking?.special_requests && (
                        <>
                            <div className="divider"></div>
                            <div className="special-requests">
                                <h4>Special Requests:</h4>
                                <p>{booking.special_requests}</p>
                            </div>
                        </>
                    )}

                    <div className="email-notice">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                            <polyline points="22 7 12 13 2 7"></polyline>
                        </svg>
                        <p>A confirmation email has been sent to <strong>{booking?.customer_email || booking?.customerEmail}</strong></p>
                    </div>
                </div>

                <div className="action-buttons">
                    <Link
                        to={`/invoice/${booking?.bookingId || booking?.booking_id || bookingId}?type=camping&download=1`}
                        state={{ booking, type: 'camping' }}
                        className="btn-secondary"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download Invoice
                    </Link>
                    <Link to="/camping" className="btn-secondary">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"></path>
                        </svg>
                        Browse More Campsites
                    </Link>
                    <Link to="/" className="btn-primary">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-5v-8H9v8H4a2 2 0 0 1-2-2z"></path>
                        </svg>
                        Go to Homepage
                    </Link>
                </div>

                <div className="help-section">
                    <p>Need help? Contact us at <a href="mailto:support@redgotrips.com">support@redgotrips.com</a> or call +91 9335444047</p>
                </div>
            </div>

            <style>{`
                .camping-booking-success-page {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 2rem;
                }
                
                .camping-success-container {
                    max-width: 700px;
                    width: 100%;
                    background: white;
                    border-radius: 32px;
                    padding: 2.5rem;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                    animation: slideUp 0.5s ease;
                }
                
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .success-icon {
                    width: 80px;
                    height: 80px;
                    background: #dcfce7;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1.5rem;
                }
                
                .success-icon svg {
                    stroke: #2e7d32;
                    stroke-width: 1.5;
                }
                
                h1 {
                    text-align: center;
                    color: #1e293b;
                    font-size: 1.8rem;
                    margin-bottom: 0.5rem;
                }
                
                .success-message {
                    text-align: center;
                    color: #64748b;
                    margin-bottom: 2rem;
                }
                
                .booking-card {
                    background: #f8fafc;
                    border-radius: 20px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                }
                
                .booking-card h3 {
                    color: #1e293b;
                    font-size: 1.1rem;
                    margin-bottom: 1rem;
                }
                
                .booking-info-grid {
                    display: grid;
                    grid-template-columns: 1fr 2fr;
                    gap: 0.75rem;
                }
                
                .info-row {
                    display: contents;
                }
                
                .info-row .label {
                    color: #64748b;
                    font-size: 0.85rem;
                    font-weight: 500;
                }
                
                .info-row .value {
                    color: #1e293b;
                    font-size: 0.9rem;
                    font-weight: 500;
                }
                
                .camping-badge {
                    display: inline-block;
                    background: #e8f5e9;
                    color: #2e7d32;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                
                .divider {
                    height: 1px;
                    background: #e2e8f0;
                    margin: 1.5rem 0;
                }
                
                .price-summary {
                    background: white;
                    border-radius: 12px;
                    padding: 1rem;
                }
                
                .price-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 0.5rem 0;
                    color: #475569;
                }
                
                .price-row.total-price {
                    font-weight: 700;
                    color: #2e7d32;
                    font-size: 1.1rem;
                }
                
                .price-row.paid {
                    border-top: 1px solid #e2e8f0;
                    margin-top: 0.5rem;
                    padding-top: 0.75rem;
                    font-weight: 600;
                    color: #1e293b;
                }
                
                .special-requests {
                    margin-top: 1rem;
                }
                
                .special-requests h4 {
                    font-size: 0.85rem;
                    color: #475569;
                    margin-bottom: 0.5rem;
                }
                
                .special-requests p {
                    font-size: 0.9rem;
                    color: #1e293b;
                    background: white;
                    padding: 0.75rem;
                    border-radius: 12px;
                }
                
                .email-notice {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-top: 1.5rem;
                    padding: 1rem;
                    background: #e8f5e9;
                    border-radius: 12px;
                }
                
                .email-notice svg {
                    stroke: #2e7d32;
                    flex-shrink: 0;
                }
                
                .email-notice p {
                    color: #166534;
                    font-size: 0.85rem;
                    margin: 0;
                }
                
                .action-buttons {
                    display: flex;
                    gap: 1rem;
                    margin-top: 1.5rem;
                }
                
                .btn-primary, .btn-secondary {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 0.85rem;
                    border-radius: 40px;
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.9rem;
                    transition: all 0.3s;
                }
                
                .btn-primary {
                    background: #2e7d32;
                    color: white;
                }
                
                .btn-primary:hover {
                    background: #1b5e20;
                    transform: translateY(-2px);
                }
                
                .btn-secondary {
                    background: #f1f5f9;
                    color: #475569;
                }
                
                .btn-secondary:hover {
                    background: #e2e8f0;
                    transform: translateY(-2px);
                }
                
                .help-section {
                    text-align: center;
                    margin-top: 2rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid #e2e8f0;
                }
                
                .help-section p {
                    color: #94a3b8;
                    font-size: 0.8rem;
                }
                
                .help-section a {
                    color: #2e7d32;
                    text-decoration: none;
                }
                
                .help-section a:hover {
                    text-decoration: underline;
                }
                
                .camping-success-loading {
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                }
                
                .spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #e2e8f0;
                    border-top-color: #2e7d32;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 1rem;
                }
                
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                
                @media (max-width: 640px) {
                    .camping-success-container {
                        padding: 1.5rem;
                    }
                    
                    .booking-info-grid {
                        grid-template-columns: 1fr;
                        gap: 0.5rem;
                    }
                    
                    .info-row {
                        display: flex;
                        justify-content: space-between;
                        padding: 0.5rem 0;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    
                    .action-buttons {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

export default CampingBookingSuccess;
